/**
 * Locize cache configuration helpers (QM-3418).
 *
 * Locize bills per private download, and the app previously re-downloaded every
 * namespace on each page load. These pure helpers decide whether to wrap the
 * Locize backend with a localStorage cache (via i18next-chained-backend) so the
 * billed download only happens on a cache miss/expiry.
 *
 * The logic is kept side-effect free (backend classes are injected, env is read
 * by the caller) so it can be unit-tested without booting i18next.
 *
 * @see https://www.locize.com/docs/caching/alternative-caching-with-i18next
 */

/**
 * Parses the raw VITE_LOCIZE_CACHE_MAX_AGE_MS env value into a non-negative
 * cache window in milliseconds. Any unset, non-numeric, zero, or negative value
 * disables caching (returns 0).
 *
 * @param {string|undefined} rawEnvValue - Raw env string (e.g. '86400000').
 * @returns {number} Cache window in ms (>0 to enable caching), or 0 to disable.
 */
export const parseLocizeCacheMaxAge = (rawEnvValue) => {
    const parsed = Number(rawEnvValue)

    if (!Number.isFinite(parsed) || parsed <= 0) {
        return 0
    }

    return Math.floor(parsed)
}

/**
 * Builds the i18next backend descriptor for the Locize integration.
 *
 * When `cacheMaxAgeMs <= 0`, returns the plain Locize backend (today's behavior:
 * every load fetches from Locize). When `cacheMaxAgeMs > 0`, returns a chained
 * backend that reads localStorage first and only falls through to Locize on a
 * cache miss or expiry. `cacheHitMode` is intentionally left at its default
 * ('none') so a cache hit never triggers a background Locize re-fetch.
 *
 * @param {Object} params
 * @param {number} params.cacheMaxAgeMs - Cache window in ms; <=0 disables caching.
 * @param {Object} params.locizeOptions - Options for i18next-locize-backend.
 * @param {Function} params.locizeBackend - The i18next-locize-backend class.
 * @param {Function} params.localStorageBackend - The i18next-localstorage-backend class.
 * @param {Function} params.chainedBackend - The i18next-chained-backend class.
 * @returns {{ plugin: Function, backend: Object }} The i18next plugin to register
 *   and the `backend` init option to pass to `i18n.init`.
 */
export const buildLocizeBackend = ({
    cacheMaxAgeMs,
    chainedBackend,
    localStorageBackend,
    locizeBackend,
    locizeOptions,
}) => {
    if (cacheMaxAgeMs <= 0) {
        return {
            plugin: locizeBackend,
            backend: locizeOptions,
        }
    }

    return {
        plugin: chainedBackend,
        backend: {
            backends: [localStorageBackend, locizeBackend],
            backendOptions: [{ expirationTime: cacheMaxAgeMs }, locizeOptions],
        },
    }
}

/**
 * Resolves the backend instance that implements Locize's `getLanguages` method.
 *
 * `i18n.services.backendConnector.backend` is the plain Locize backend when
 * caching is disabled, but the wrapping `i18next-chained-backend` when it is
 * enabled. The chained backend only proxies the standard i18next interface
 * (read/create/save), not Locize extras like `getLanguages`, but it keeps the
 * instantiated sub-backends in its `backends` array. This finds the one that can
 * list project languages, so callers work regardless of caching.
 *
 * @param {Object} backend - The active `backendConnector.backend` instance.
 * @returns {Object|undefined} The backend exposing `getLanguages`, or undefined.
 */
export const resolveLocizeBackend = (backend) => {
    if (backend) {
        if (typeof backend.getLanguages === 'function') {
            return backend
        }

        if (Array.isArray(backend.backends)) {
            const nested = backend.backends.find((candidate) => typeof candidate?.getLanguages === 'function')

            if (nested) {
                return nested
            }
        }
    }

    // Surface the miss instead of silently no-opting: callers use the result to
    // populate the language list, so a silent undefined would look like "no
    // languages" with no clue why.
    console.warn(
        'resolveLocizeBackend: no backend exposing getLanguages was found; the language list will not be populated.'
    )

    return undefined
}
